import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { config } from '../../../environments/environment';
import { Router } from '@angular/router';
import { ToasterService } from '../../core/services/toaster.service';
import { LoggerService } from '../../core/services/logger.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  permissions: string[] = [];
  // Add this BehaviorSubject to notify components when permissions change
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  public permissionsChanged = this.permissionsSubject.asObservable();

  // Call this method in the constructor
  constructor(
    private http: HttpClient,
    private router: Router,
    private toaster: ToasterService,
    private logger: LoggerService,
    private translate: TranslateService
  ) {
    // Load any saved permissions when service initializes
    this.loadSavedPermissions();
  }
  private async createDerivedKey(token: string): Promise<Uint8Array> {
    // Use the browser's built-in crypto API
    const encoder = new TextEncoder();
    const data = encoder.encode(token);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert to Uint8Array
    return new Uint8Array(hashBuffer);
  }
  async xorDecrypt(encryptedText: string, token: string): Promise<string> {
    try {
      if (!encryptedText) return '';

      // Get the derived key (same method as server-side)
      const derivedKey = await this.createDerivedKey(token);

      // Convert Base64 to bytes
      const encryptedBytes = this.base64ToBytes(encryptedText);
      const decryptedBytes = new Uint8Array(encryptedBytes.length);

      // XOR with the derived key
      for (let i = 0; i < encryptedBytes.length; i++) {
        decryptedBytes[i] =
          encryptedBytes[i] ^ derivedKey[i % derivedKey.length];
      }

      // Convert back to string
      const decoder = new TextDecoder();
      return decoder.decode(decryptedBytes);
    } catch (e) {
      console.error('Decryption failed:', e);
      return '';
    }
  }
  async xorEncrypt(plaintext: string, token: string): Promise<string> {
    try {
      if (!plaintext) return '';

      // Get the derived key (same method as in decrypt)
      const derivedKey = await this.createDerivedKey(token);

      // Convert plaintext to bytes
      const encoder = new TextEncoder();
      const plaintextBytes = encoder.encode(plaintext);
      const encryptedBytes = new Uint8Array(plaintextBytes.length);

      // XOR with the derived key
      for (let i = 0; i < plaintextBytes.length; i++) {
        encryptedBytes[i] =
          plaintextBytes[i] ^ derivedKey[i % derivedKey.length];
      }

      // Convert to Base64
      return this.bytesToBase64(encryptedBytes);
    } catch (e) {
      console.error('Encryption failed:', e);
      return '';
    }
  }

  // Helper method to convert bytes to Base64
  private bytesToBase64(bytes: Uint8Array): string {
    const binString = Array.from(bytes)
      .map((byte) => String.fromCharCode(byte))
      .join('');
    return btoa(binString);
  }

  // Helper method to convert Base64 to bytes (needed by xorDecrypt)
  private base64ToBytes(base64: string): Uint8Array {
    const binString = atob(base64);
    const bytes = new Uint8Array(binString.length);
    for (let i = 0; i < binString.length; i++) {
      bytes[i] = binString.charCodeAt(i);
    }
    return bytes;
  }
  storePermissions(permissions: string[]): void {
    // Store in localStorage
    localStorage.setItem('userPermissions', JSON.stringify(permissions));

    // Update the class property
    this.permissions = permissions;

    // Notify all components that permissions have changed
    this.permissionsSubject.next(permissions);
  }
  // Update your login method's permission handling
  async login(email: string, password: string): Promise<Observable<any>> {
    const encryptedEmail = await this.xorEncrypt(email, config.encryptionKey);
    const encryptedPassword = await this.xorEncrypt(
      password,
      config.encryptionKey
    );
    const body = { email: encryptedEmail, password: encryptedPassword };
    return this.http.post<any>(`${config.endPointURL}User/Login`, body).pipe(
      tap(async (response) => {
          if (response && response.isSuccess) {
          if (response.response.user.userType == 'Entity Admin') {
            this.toaster.onFailure(
              this.translate.instant('titles.error'),
              this.translate.instant('errorMessages.unauthorizedPortal')
            );
            return;
          } else {
            this.storeToken(response.response.token.accessToken);
            this.storeEntityId(response.response.user.entityId);
            // Store entity type if provided by backend
            if (response.response.user.entityType) {
              this.storeEntityType(response.response.user.entityType);
            }
            let encryptedPermissions: string[] =
              response.response.user.permissions || [];

            const permissionPromises = encryptedPermissions.map((p) =>
              this.xorDecrypt(p, response.response.token.accessToken)
            );
            const decryptedPermissions = await Promise.all(permissionPromises);

            // IMPORTANT: Set this.permissions directly
            this.permissions = decryptedPermissions;

            // Store in localStorage using the fixed storePermissions
            this.storePermissions(decryptedPermissions);

            this.toaster.onSuccess(
              this.translate.instant('titles.success'),
              this.translate.instant('successMessages.loggedIn')
            );
          }
        } else {
          throw new Error(
            response.message || this.translate.instant('errorMessages.loginFailed')
          );
        }
      }),
      catchError(this.handleError)
    );
  }

  loadSavedPermissions(): void {
    const savedPermissions = localStorage.getItem('userPermissions');
    if (savedPermissions) {
      try {
        const parsedPermissions = JSON.parse(savedPermissions);
        this.permissions = parsedPermissions;

        // Also notify components about the loaded permissions
        this.permissionsSubject.next(parsedPermissions);

      } catch (e) {
        this.permissions = [];
        this.permissionsSubject.next([]);
      }
    } else {
      this.permissions = [];
      this.permissionsSubject.next([]);
    }
  }
  logoutAPI(): Observable<any> {
    return this.http.post<any>(`${config.endPointURL}User/Logout`, null).pipe(
      tap((response) => {
        if (response && response.isSuccess) {
          this.toaster.onSuccess(
            this.translate.instant('titles.success'),
            this.translate.instant('successMessages.loggedOut')
          );
        } else {
          throw new Error(
            response.message || this.translate.instant('errorMessages.logoutFailed')
          );
        }
      }),
      catchError(this.handleError)
    );
  }

  forgetPassword(emailAddress: string): Observable<any> {
    const body = { emailAddress };

    return this.http
      .post<any>(`${config.endPointURL}User/SendForgetPasswordEmail/1`, body)
      .pipe(
        tap((response) => {
          if (response && response.isSuccess) {
            return response;
          } else {
            throw new Error(response.message || this.translate.instant('errorMessages.noUserFound'));
          }
        }),
        catchError((error) => {
          console.error('Server error:', error);
          return throwError(error);
        })
      );
  }

  async resetPassword(token: string, newPassword: string): Promise<Observable<any>> {
    const encryptedPassword = await this.xorEncrypt(
      newPassword,
      config.encryptionKey
    );
    const body = { token, newPassword: encryptedPassword };

    return this.http
      .post<any>(`${config.endPointURL}User/ResetPassword`, body)
      .pipe(
        tap((response) => {
          if (response && response.isSuccess) {
            return response;
          } else {
            throw new Error(response.message || this.translate.instant('errorMessages.resetPasswordFailed'));
          }
        }),
        catchError((error) => {
          console.error('Server error:', error);
          return throwError(error);
        })
      );
  }

  private handleError(error: HttpErrorResponse | Error): Observable<never> {
    let errorMessage: string;

    if (error instanceof Error) {
      // Custom error thrown by the application
      errorMessage = error.message;
    } else if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Server-side error: ${error.message}`;
      if (error.error && error.error.message) {
        errorMessage = `Error Code: ${error.status}\nMessage: ${error.error.message}`;
      }
    }

    console.error('Error from server:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  private storeToken(token: string): void {
    this.logger.debug('Storing access token', token);
    sessionStorage.setItem('accessToken', token);
  }

  getToken(): string | null {
    return sessionStorage.getItem('accessToken');
  }

  // Removed duplicate implementation of storePermissions
  hasPermission(permission: string): boolean {
    // First check in-memory permissions (most up-to-date)
    if (this.permissions && this.permissions.includes(permission)) {
      return true;
    }

    // Fall back to localStorage permissions
    const storedPermissions = JSON.parse(
      localStorage.getItem('userPermissions') || '[]'
    );
    return storedPermissions.includes(permission);
  }
  clearPermissions(): void {
    localStorage.removeItem('userPermissions');
  }

  private storeEntityId(token: string): void {
    console.log(token);
    sessionStorage.setItem('entityId', token);
  }

  getEntityId(): string | null {
    return sessionStorage.getItem('entityId');
  }

  // Store the entity type for later UI logic (e.g., disabling menu items for Regulators)
  private storeEntityType(entityType: string): void {
    sessionStorage.setItem('entityType', entityType);
  }

  getEntityType(): string | null {
    return sessionStorage.getItem('entityType');
  }

  private isHandlingSessionExpiry = false;

  handleSessionExpiry(): void {
    if (this.isHandlingSessionExpiry) {
      return;
    }
    this.isHandlingSessionExpiry = true;
    this.clearAllSessionData();
    this.toaster.onFailure(
      this.translate.instant('titles.error'),
      this.translate.instant('errorMessages.sessionExpired')
    );
    this.router.navigateByUrl('/auth/login').finally(() => {
      this.isHandlingSessionExpiry = false;
    });
  }

  logout(isTokenExpired: boolean = false): void {
    if (isTokenExpired) {
      this.handleSessionExpiry();
    } else {
      this.logoutAPI().subscribe({
        next: () => {
          this.clearAllSessionData();
        },
        error: (error) => {
          this.toaster.onFailure(
            this.translate.instant('titles.error'),
            error.message || this.translate.instant('errorMessages.logoutUnknown')
          );
          this.clearAllSessionData();
        },
      });
    }
  }

  private clearAllSessionData(): void {
    sessionStorage.removeItem('entityId');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('entityType');
    this.clearPermissions();
    this.router.navigateByUrl('/auth/login');
  }
}
